/**
 * Drug data access layer.
 * Uses RvLite's native vector API — NOT SQL queries.
 * RvLite stores drugs as: insert_with_id(id, vector, metadata).
 * Retrieval is via get(id) which returns the metadata JSON.
 */

import type { Drug } from '../types/index';
import { getDb, searchVectors, getDrugFromCache, searchDrugsFromCache, getAllDrugsFromCache } from './db';

// ---------------------------------------------------------------------------
// Row parsing helpers
// ---------------------------------------------------------------------------

function parseJsonArray(raw: unknown): string[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [String(parsed)];
    } catch {
      return raw.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }
  return [];
}

/**
 * Convert RvLite's native get() result (metadata object) into a typed Drug.
 */
function metadataToDrug(id: string, meta: Record<string, unknown>): Drug {
  return {
    id,
    generic_name: String(meta['generic_name'] ?? ''),
    brand_names: parseJsonArray(meta['brand_names']),
    description: String(meta['description'] ?? ''),
    dosage_forms: parseJsonArray(meta['dosage_forms']),
    strengths: parseJsonArray(meta['strengths']),
    dosage_instructions: String(meta['dosage_instructions'] ?? ''),
    side_effects: parseJsonArray(meta['side_effects']),
    warnings: parseJsonArray(meta['warnings']),
    food_interactions: parseJsonArray(meta['food_interactions']),
    storage: String(meta['storage'] ?? ''),
    missed_dose: String(meta['missed_dose'] ?? ''),
    pill_image_path: String(meta['pill_image_path'] ?? ''),
    pill_color: String(meta['pill_color'] ?? ''),
    pill_shape: String(meta['pill_shape'] ?? ''),
    pill_imprint: String(meta['pill_imprint'] ?? ''),
    vector: undefined,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Retrieve a single drug by its primary key (RxNorm CUI).
 * Uses localStorage cache first (always available), falls back to RvLite.
 */
export async function getDrugById(id: string): Promise<Drug | null> {
  // PRIMARY: Try localStorage cache (works even if WASM is unavailable).
  const cached = getDrugFromCache(id);
  if (cached) return metadataToDrug(id, cached);

  // FALLBACK: Try RvLite if available.
  const db = await getDb();
  if (!db) return null;

  try {
    const result = db.get(id);
    if (!result) return null;
    const meta = (result as Record<string, unknown>).metadata ?? result;
    return metadataToDrug(id, meta as Record<string, unknown>);
  } catch {
    return null;
  }
}

/**
 * Search drugs by name. Uses localStorage cache (primary), falls back to RvLite.
 */
export async function searchDrugsByName(query: string): Promise<Drug[]> {
  // PRIMARY: Search localStorage cache.
  const cacheResults = searchDrugsFromCache(query);
  if (cacheResults.length > 0) {
    return cacheResults
      .map(({ id, meta }) => metadataToDrug(id, meta))
      .sort((a, b) => a.generic_name.localeCompare(b.generic_name));
  }

  // FALLBACK: Try RvLite.
  const db = await getDb();
  if (!db) return [];

  try {
    const exported = db.export_json();
    if (!exported) return [];

    const results: Drug[] = [];
    const entries = (exported as Record<string, unknown>).vectors ??
                    (exported as Record<string, unknown>).entries ??
                    exported;
    const lowerQuery = query.toLowerCase();

    if (Array.isArray(entries)) {
      for (const entry of entries) {
        const e = entry as Record<string, unknown>;
        const meta = (e.metadata ?? e) as Record<string, unknown>;
        const id = String(e.id ?? meta.id ?? '');
        const genericName = String(meta.generic_name ?? '').toLowerCase();
        const brandNames = JSON.stringify(meta.brand_names ?? []).toLowerCase();

        if (genericName.includes(lowerQuery) || brandNames.includes(lowerQuery)) {
          results.push(metadataToDrug(id, meta));
        }
      }
    }

    return results.sort((a, b) => a.generic_name.localeCompare(b.generic_name));
  } catch (err) {
    console.warn('[AfterCare] searchDrugsByName failed:', err);
    return [];
  }
}

/**
 * Semantic search using HNSW vector index.
 */
export async function searchDrugsBySemantic(
  queryVector: Float32Array,
  limit: number = 10,
): Promise<Drug[]> {
  const matches = await searchVectors(queryVector, limit);
  if (!matches || matches.length === 0) return [];

  const drugs: Drug[] = [];
  for (const match of matches) {
    const drug = await getDrugById(match.id);
    if (drug) drugs.push(drug);
  }

  return drugs;
}

/**
 * Return every drug in the catalog.
 */
export async function getAllDrugs(): Promise<Drug[]> {
  // PRIMARY: Use localStorage cache.
  const cacheResults = getAllDrugsFromCache();
  if (cacheResults.length > 0) {
    return cacheResults
      .map(({ id, meta }) => metadataToDrug(id, meta))
      .sort((a, b) => a.generic_name.localeCompare(b.generic_name));
  }

  // FALLBACK: Try RvLite.
  const db = await getDb();
  if (!db) return [];

  try {
    const exported = db.export_json();
    if (!exported) return [];

    const results: Drug[] = [];
    const entries = (exported as Record<string, unknown>).vectors ??
                    (exported as Record<string, unknown>).entries ??
                    exported;

    if (Array.isArray(entries)) {
      for (const entry of entries) {
        const e = entry as Record<string, unknown>;
        const meta = (e.metadata ?? e) as Record<string, unknown>;
        const id = String(e.id ?? meta.id ?? '');
        if (id) results.push(metadataToDrug(id, meta));
      }
    }

    return results.sort((a, b) => a.generic_name.localeCompare(b.generic_name));
  } catch (err) {
    console.warn('[AfterCare] getAllDrugs failed:', err);
    return [];
  }
}

/**
 * Find a drug by brand name (case-insensitive).
 */
export async function getDrugByBrandName(name: string): Promise<Drug | null> {
  const results = await searchDrugsByName(name);
  return results[0] ?? null;
}
